<template>
  <div class="space-y-4">
    <h2>{{ t('ignoredHolidays') }}</h2>

    <div class="space-y-4 rounded-md bg-white pb-4 pl-4 pr-4 pt-2">
      <div v-if="!fields.length" class="flex justify-center p-4">
        {{ t('noEntries') }}
      </div>

      <div
        v-for="(field, i) in fields"
        :key="i"
        class="relative space-y-4 border-b pb-3 pt-3 last:border-b-0 last:pb-0"
      >
        <div class="grid grid-cols-3 gap-4">
          <Select
            :model-value="field.value.holidayType"
            :label="t('holidayType')"
            :options="holidayTypeOptions"
            @update:model-value="handleHolidayTypeChange(i, $event as string)"
          />

          <template v-if="field.value.holidayType === 'specificDate'">
            <DatepickerField
              :name="`ignoredHolidays[${i}].holiday.specificDate`"
              :label="t('specificDate')"
            />
          </template>

          <template v-if="field.value.holidayType === 'publicHoliday'">
            <SelectField
              :name="`ignoredHolidays[${i}].holiday.rule`"
              :label="t('holiday')"
              :options="holidayRuleOptions"
            />
            <SelectField
              :name="`ignoredHolidays[${i}].holiday.year`"
              :label="t('year')"
              :options="yearOptions"
              empty-value-to="undefined"
              type="number"
            />
          </template>
        </div>

        <div v-if="field.value.holidayType === 'specificDate'">
          <Checkbox
            :model-value="!!field.value.isClosed"
            @update:model-value="handleIsClosedToggle(i, $event)"
          >
            {{ t('isClosed') }}
          </Checkbox>
        </div>

        <div>
          <Checkbox
            :model-value="!!field.value.alternativeOpeningHours"
            :disabled="!!field.value.isClosed"
            @update:model-value="handleAlternativeHoursToggle(i, $event)"
          >
            {{ t('hasAlternativeOpeningHours') }}
          </Checkbox>
        </div>

        <div v-if="field.value.alternativeOpeningHours" class="rounded-md bg-obsidian-50 p-4">
          <OpeningRangesInput
            :model-value="field.value.alternativeOpeningHours.ranges"
            @update:model-value="handleOpeningRangesChange(i, $event)"
          />
        </div>

        <div class="flex justify-end">
          <button
            class="absolute right-0 top-0 rounded-md p-2 text-bittersweet-600 transition hover:bg-bittersweet-50"
            :disabled="fields.length === 0"
            @click.prevent="removeItem(i)"
          >
            <XIcon class="size-4" />
          </button>
        </div>
      </div>

      <InputHint :hint="error" is-error />

      <div class="flex justify-center">
        <CVButton variant="ghost" @click.prevent="addItem">{{ t('addIgnoredHoliday') }}</CVButton>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { useI18n } from 'vue-i18n';
import { useFieldArray, useFieldError, useFormContext } from 'vee-validate';
import Checkbox from '@/components/Checkbox.vue';
import SelectField from '@/components/SelectField.vue';
import Select from '@/components/Select.vue';
import InputHint from '@/components/InputHint.vue';
import { XIcon } from 'lucide-vue-next';
import { Alert } from '@/services/alert.service';
import { computed } from 'vue';
import CVButton from '@/components/buttons/CVButton.vue';
import DatepickerField from '@/components/DatepickerField.vue';
import type { Station } from '@/entities/station/station.entity';
import { getLocalizedField } from '@/utils/get-localized-field';
import OpeningRangesInput from './OpeningRangesInput.vue';
import { useAvailableHolidayRules } from '@/queries/use-stations';
import type { IgnoredHolidayDto, TimeRange } from '@carvia/ros-client-types';

const { t, locale } = useI18n();

defineProps<{ station?: Station }>();

const { fields, push, remove, update } = useFieldArray<IgnoredHolidayDto & { holidayType: string }>(
  'ignoredHolidays',
);
const { values: formValues } = useFormContext<Station>();

const error = useFieldError('ignoredHolidays');

const { data: availableHolidayRules } = useAvailableHolidayRules(
  computed(() => formValues.countryCode),
  computed(() => formValues.state),
  computed(() => !!formValues.countryCode),
);

const holidayTypeOptions = computed(() => [
  { label: t('publicHoliday'), value: 'publicHoliday' },
  { label: t('specificDate'), value: 'specificDate' },
]);

const holidayRuleOptions = computed(() =>
  (availableHolidayRules.value ?? []).map((rule) => ({
    label: getLocalizedField(rule.name, locale.value),
    value: rule.rule,
  })),
);

const yearOptions = computed(() => [
  { label: t('everyYear'), value: undefined },
  ...Array.from({ length: 20 }, (_, i) => ({
    label: (new Date().getFullYear() - 5 + i).toString(),
    value: (new Date().getFullYear() - 5 + i).toString(),
  })),
]);

const handleHolidayTypeChange = (i: number, type: string) => {
  const current: any = fields.value?.[i]?.value ?? {};
  const next = {
    ...current,
    holidayType: type,
    isClosed: type === 'specificDate' ? current.isClosed : undefined,
    holiday:
      type === 'specificDate' ? { specificDate: undefined } : { rule: undefined, year: undefined },
  };
  update(i, next);
};

const handleIsClosedToggle = (i: number, isClosed: boolean) => {
  update(i, {
    ...fields.value[i].value,
    isClosed: isClosed || undefined,
    alternativeOpeningHours: isClosed ? undefined : fields.value[i].value.alternativeOpeningHours,
  });
};

const handleAlternativeHoursToggle = (i: number, hasHours: boolean) => {
  update(i, {
    ...fields.value[i].value,
    isClosed: hasHours ? undefined : fields.value[i].value.isClosed,
    alternativeOpeningHours: hasHours ? { ranges: [{ start: 9 * 60, end: 17 * 60 }] } : undefined,
  });
};

const handleOpeningRangesChange = (i: number, ranges: TimeRange[]) => {
  update(i, {
    ...fields.value[i].value,
    alternativeOpeningHours: { ranges },
  });
};

const addItem = () => {
  push({
    holidayType: 'publicHoliday',
    holiday: {
      rule: undefined as any,
      year: undefined,
    },
  });
};

const removeItem = async (i: number) => {
  const { isDenied } = await Alert.fire({
    text: t('reallyDeleteMessage'),
    icon: 'warning',
    showConfirmButton: false,
    showDenyButton: true,
    showCancelButton: true,
    denyButtonText: t('delete'),
    cancelButtonText: t('cancel'),
  });
  if (!isDenied) {
    return;
  }
  remove(i);
};
</script>

<i18n lang="json">
{
  "en": {
    "ignoredHolidays": "Ignored Holidays",
    "addIgnoredHoliday": "+ Add ignored holiday",
    "noEntries": "No Entries",
    "holidayType": "Holiday Type",
    "specificDate": "Specific Date",
    "publicHoliday": "Public Holiday",
    "holiday": "Holiday",
    "year": "Year (optional)",
    "isClosed": "Station is closed",
    "hasAlternativeOpeningHours": "Has alternative opening hours",
    "reallyDeleteMessage": "Do you really want to delete this item?",
    "delete": "Delete",
    "cancel": "Cancel",
    "everyYear": "Every Year"
  },
  "de": {
    "ignoredHolidays": "Ignorierte Feiertage",
    "addIgnoredHoliday": "+ Ignorierten Feiertag hinzufügen",
    "noEntries": "Keine Einträge",
    "holidayType": "Feiertagstyp",
    "specificDate": "Bestimmtes Datum",
    "publicHoliday": "Öffentlicher Feiertag",
    "holiday": "Feiertag",
    "year": "Jahr (optional)",
    "isClosed": "Station ist geschlossen",
    "hasAlternativeOpeningHours": "Hat alternative Öffnungszeiten",
    "reallyDeleteMessage": "Möchtest du diesen Eintrag wirklich löschen?",
    "delete": "Löschen",
    "cancel": "Abbrechen",
    "everyYear": "Jedes Jahr"
  },
  "es": {
    "ignoredHolidays": "Festivos ignorados",
    "addIgnoredHoliday": "+ Añadir festivo ignorado",
    "noEntries": "Sin entradas",
    "holidayType": "Tipo de festivo",
    "specificDate": "Fecha concreta",
    "publicHoliday": "Festivo oficial",
    "holiday": "Festivo",
    "year": "Año (opcional)",
    "hasAlternativeOpeningHours": "Tiene horario alternativo",
    "reallyDeleteMessage": "¿Seguro que desea eliminar esta entrada?",
    "delete": "Eliminar",
    "cancel": "Cancelar",
    "everyYear": "Cada año"
  }
}
</i18n>
